"use client";
import { useState, useEffect } from "react";
import {
  Plus,
  Tag,
  Trash2,
  Edit3,
  Check,
  X,
  Folder,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import Input from "../../../components/admin/common/Input";
import Button from "../../../components/admin/common/Button";
import {
  createCategory,
  getAllCategories,
} from "../../../services/categoryServices";

const AddCategory = () => {
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCategories = async () => {
    try {
      const data = await getAllCategories();
      setCategories(data.data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAdd = async () => {
    if (category.trim()) {
      try {
        await createCategory({ name: category.trim() });
        toast.success("Category added");
        setCategory("");
        fetchCategories();
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleAdd();
  };

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <div className="sticky top-0 h-screen text-white">
          <Sidebar />
        </div>
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              <Header
                title="Add Category"
                desc="Add and manage event categories"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
                {/* Add New Category */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-[#4f22d8]/20 space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-[#4f22d8] to-[#3b1ba3] rounded-lg">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      Add New Category
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Category Name
                      </label>
                      <Input
                        placeholder="Enter category name..."
                        value={category}
                        onChange={setCategory}
                        onKeyPress={handleKeyPress}
                        className="focus:border-[#4f22d8] focus:ring-[#4f22d8]/20"
                      />
                    </div>

                    <Button
                      onClick={handleAdd}
                      disabled={!category.trim()}
                      className="w-full bg-gradient-to-r from-[#4f22d8] to-[#3b1ba3] hover:from-[#3b1ba3] hover:to-[#2d1377] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200"
                    >
                      <Plus className="w-5 h-5" />
                      Add Category
                    </Button>
                  </div>
                </div>

                {/* Category List */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-[#4f22d8]/20">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-[#4f22d8] to-[#3b1ba3] rounded-lg">
                          <Folder className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">
                          Categories ({categories.length})
                        </h2>
                      </div>

                      {categories.length > 0 && (
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-11 pr-4 h-10 border border-gray-200 rounded-lg focus:border-[#4f22d8] focus:ring-2 focus:ring-[#4f22d8]/20 outline-none text-sm w-48"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      {categories.length === 0 ? (
                        <div className="text-center py-8 flex flex-col items-center justify-center h-full">
                          <div className="p-4 bg-[#4f22d8]/10 rounded-full w-20 h-20 mb-4 flex items-center justify-center">
                            <Tag className="w-8 h-8 text-[#4f22d8]" />
                          </div>
                          <p className="text-gray-500 text-lg font-medium">
                            No categories yet
                          </p>
                          <p className="text-gray-400 mt-1">
                            Add your first category
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                          {categories
                            .filter((cat) =>
                              cat.label
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                            )
                            .map((cat, index) => (
                              <div
                                key={cat.value}
                                className="flex items-center justify-between p-4 h-20 bg-gradient-to-r from-[#4f22d8]/5 to-[#4f22d8]/10 rounded-lg border border-[#4f22d8]/20 hover:border-[#4f22d8]/40 transition-all duration-200 hover:shadow-md group"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="p-2 bg-[#4f22d8]/10 rounded-lg group-hover:bg-[#4f22d8]/20 transition-colors duration-200 shrink-0">
                                    <Tag className="w-4 h-4 text-[#4f22d8]" />
                                  </div>
                                  {editingIndex === index ? (
                                    <input
                                      type="text"
                                      value={editingValue}
                                      onChange={(e) =>
                                        setEditingValue(e.target.value)
                                      }
                                      className="px-3 py-2 border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none bg-white flex-1 min-w-0"
                                      autoFocus
                                    />
                                  ) : (
                                    <span className="text-gray-800 font-medium truncate">
                                      {cat.label}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 ml-2">
                                  {editingIndex === index ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          toast.success("Edit saved locally");
                                          setEditingIndex(-1);
                                        }}
                                        className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors duration-200"
                                      >
                                        <Check className="w-4 h-4 text-green-600" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingIndex(-1);
                                          setEditingValue("");
                                        }}
                                        className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                                      >
                                        <X className="w-4 h-4 text-gray-600" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => {
                                          setEditingIndex(index);
                                          setEditingValue(cat.label);
                                        }}
                                        className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors duration-200 opacity-0 group-hover:opacity-100"
                                      >
                                        <Edit3 className="w-4 h-4 text-blue-600" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          toast(
                                            "Delete functionality not implemented"
                                          )
                                        }
                                        className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors duration-200 opacity-0 group-hover:opacity-100"
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default AddCategory;
